/**
 * Bottom sheet list selector for mobile (like transfer dialog account/currency lists).
 * Use instead of standard dropdown for better touch UX.
 */

import { ReactElement } from "react";
import { Box, List, ListItem, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";

export interface MobileBottomListSelectItem {
  value: string;
  label: string;
  secondary?: string;
}

interface MobileBottomListSelectProps {
  open: boolean;
  title: string;
  items: MobileBottomListSelectItem[];
  selectedValue: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}

export default function MobileBottomListSelect({
  open,
  title,
  items,
  selectedValue,
  onSelect,
  onClose,
}: MobileBottomListSelectProps): ReactElement | null {
  if (!open) return null;

  const handleSelect = (value: string): void => {
    onSelect(value);
    onClose();
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(0,0,0,0.3)",
          zIndex: 1299,
        }}
        onClick={onClose}
        aria-hidden
      />
    <Paper
      elevation={0}
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: "50vh",
        maxHeight: "50vh",
        bgcolor: "background.paper",
        border: "none",
        borderTop: "2px solid",
        borderColor: "primary.main",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 -4px 20px rgba(0, 0, 0, 0.4)"
            : "0 -4px 20px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto",
        WebkitTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List sx={{ p: 0 }}>
          {items.map((item) => (
            <ListItem key={item.value} disablePadding>
              <ListItemButton
                onClick={() => handleSelect(item.value)}
                selected={selectedValue === item.value}
              >
                <ListItemText primary={item.label} secondary={item.secondary} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
    </>
  );
}
